import React, { useContext } from "react";
import { HashRouter as Router, Route, Switch } from "react-router-dom";
import {
    ErrorBoundary,
    SprintQueryClient,
    SprintQueryClientProvider,
    Toaster,
    DateRenderProvider
} from "sprint-common-web";

import { AdminRoutes } from "./modules/admin/AdminRoutes";
import { AssetsRoutes } from "./modules/assets/AssetsRoutes";
import { HomeRoutes } from "./modules/common/ui/home/HomeRoutes";
import { DefaultLayout } from "./modules/common/ui/layout/DefaultLayout";
import { Header } from "./modules/common/ui/layout/header/Header";
import { ContentIngestionRoutes } from "./modules/content-ingestion/ContentIngestionRoutes";
import { GenerateAccessKeyPage } from "./modules/content-ingestion/delivery-buckets/generate-access-key/GenerateAccessKeyPage";
import { ContentRoutes } from "./modules/content/ContentRoutes";
import { AuthConfigurationProvider } from "./modules/core/configuration/auth/AuthConfigurationProvider";
import { ConfigurationProvider } from "./modules/core/configuration/ConfigurationProvider";
import { FeatureFlagsProvider } from "./modules/core/configuration/feature-flags/FeatureFlagsProvider";
import { NavigationProvider } from "./modules/core/configuration/navigation/NavigationProvider";
import { AuthUserErrorPage } from "./modules/core/error/AuthUserErrorPage";
import { PublicRoute } from "./modules/core/routing/PublicRoute";
import { AuthContext, AuthProvider } from "./modules/core/security/AuthProvider";
import { UserProvider } from "./modules/core/security/UserProvider";
import { OrdersRoutes } from "./modules/orders/OrdersRoutes";
import { ReportsRoutes } from "./modules/reports/ReportsRoutes";

const sprintQueryClient = new SprintQueryClient();

export const App: React.FunctionComponent = () => {
    return (
        <>
            <Toaster />
            <Router>
                <Switch>
                    <Route path="/auth-user-error" exact component={AuthUserErrorPage} />
                    <PublicRoute path="/content-ingestion/invite/:token" component={<GenerateAccessKeyPage />} />
                    <AuthConfigurationProvider>
                        <AuthProvider>
                            <SprintQueryClientProvider client={sprintQueryClient}>
                                <ConfigurationProvider>
                                    <FeatureFlagsProvider>
                                        <UserProvider>
                                            <DateRenderProvider>
                                                <NavigationProvider>
                                                    <DefaultLayout header={<Header />}>
                                                        <Route path="/" component={PrivateRoutes} />
                                                    </DefaultLayout>
                                                </NavigationProvider>
                                            </DateRenderProvider>
                                        </UserProvider>
                                    </FeatureFlagsProvider>
                                </ConfigurationProvider>
                            </SprintQueryClientProvider>
                        </AuthProvider>
                    </AuthConfigurationProvider>
                </Switch>
            </Router>
        </>
    );
};

const PrivateRoutes: React.VFC = () => {
    const { isAdmin } = useContext(AuthContext);

    return (
        <ErrorBoundary showStack={isAdmin}>
            <HomeRoutes />
            <ContentIngestionRoutes />
            <ContentRoutes />
            <AssetsRoutes />
            <AdminRoutes />
            <OrdersRoutes />
            <ReportsRoutes />
        </ErrorBoundary>
    );
};
